import { useRef, useEffect, useState } from 'react'
import { useServerLogs } from '@/services/logs'
import { useGetServerInstancePodName } from '@/services/server'
import { LazyMonacoEditor } from '@/components/lazy-monaco-editor'
import { useTheme } from '@/components/theme-provider'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { RefreshCw, ChevronLeft, ChevronRight, Server } from 'lucide-react'
import { cn } from '@/lib/utils'

type ServerLogsProps = {
  instanceId: number
  containerName?: string
}

export const ServerLogs = ({
  instanceId,
  containerName = '',
}: ServerLogsProps) => {
  const { data: podNames } = useGetServerInstancePodName(instanceId)
  const [activeTab, setActiveTab] = useState<string>('')

  // Set initial active tab when podNames are available
  useEffect(() => {
    if (podNames && podNames.length > 0 && !activeTab) {
      setActiveTab(podNames[0])
    }
  }, [podNames, activeTab])

  if (!podNames || podNames.length === 0) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">
              Server Logs
            </h3>
            <span
              className="inline-block h-2 w-2 rounded-full bg-destructive"
              title="Disconnected"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
        <div className="bg-card p-4 rounded-lg text-center text-muted-foreground">
          No pods available
        </div>
      </div>
    )
  }

  if (podNames.length === 1) {
    return (
      <SinglePodLogsLayout
        instanceId={instanceId}
        podName={podNames[0]}
        containerName={containerName}
      />
    )
  }

  return (
    <ServerLogsSidebar
      instanceId={instanceId}
      podNames={podNames}
      containerName={containerName}
    />
  )
}

type ServerLogsSidebarProps = {
  instanceId: number
  podNames: string[]
  containerName: string
}

const ServerLogsSidebar = ({
  instanceId,
  podNames,
  containerName,
}: ServerLogsSidebarProps) => {
  const [activeTab, setActiveTab] = useState<string>(podNames[0])
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  // Get connection status for the active pod
  const { isConnected, reconnect } = useServerLogs(
    instanceId,
    activeTab,
    containerName
  )

  return (
    <div className="flex h-[500px] border rounded-lg overflow-hidden bg-card max-w-full">
      {/* Sidebar */}
      <div
        className={cn(
          'flex flex-col border-r bg-muted/30 transition-all duration-300 ease-in-out',
          sidebarCollapsed ? 'w-12' : 'w-64'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <Server className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">
                Replicas
              </span>
              <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                {podNames.length}
              </span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="h-6 w-6 p-0 hover:bg-muted"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-3 w-3" />
            ) : (
              <ChevronLeft className="h-3 w-3" />
            )}
          </Button>
        </div>

        {/* Pod List */}
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {podNames.map((podName) => (
              <button
                key={podName}
                onClick={() => setActiveTab(podName)}
                className={cn(
                  'w-full text-left p-2 text-sm rounded-md transition-colors duration-200 flex items-center gap-2',
                  'hover:bg-muted/60 focus:outline-none focus:ring-1 focus:ring-ring',
                  activeTab === podName
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                title={sidebarCollapsed ? podName : undefined}
              >
                <div className="w-2 h-2 rounded-full bg-current opacity-60 flex-shrink-0" />
                {!sidebarCollapsed && (
                  <span className="truncate font-mono text-xs leading-relaxed">
                    {podName.length > 25
                      ? `${podName.slice(0, 25)}...`
                      : podName}
                  </span>
                )}
              </button>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Main Content */}
      <div className="flex w-full flex-col">
        {/* Content Header */}
        <LogsHeader
          podName={activeTab}
          isConnected={isConnected}
          onReconnect={reconnect}
        />

        {/* Logs Content */}
        <div className="h-full p-3 min-w-0 max-w-3xl w-auto">
          <LogsTabContent
            instanceId={instanceId}
            podName={activeTab}
            containerName={containerName}
            showHeading={false}
            showHeader={false}
          />
        </div>
      </div>
    </div>
  )
}

type LogsHeaderProps = {
  podName: string
  isConnected: boolean
  onReconnect: () => void
}

const LogsHeader = ({ podName, isConnected, onReconnect }: LogsHeaderProps) => (
  <div className="flex items-center justify-between p-3 border-b">
    <div className="flex items-center gap-2">
      <h3 className="text-sm font-semibold text-foreground">Server Logs</h3>
      <span
        className={`inline-block h-2 w-2 rounded-full ${
          isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-destructive'
        }`}
        title={isConnected ? 'Live • Connected' : 'Disconnected'}
      />
      <span className="text-xs font-mono bg-muted px-2 py-1 rounded text-muted-foreground">
        {podName}
      </span>
    </div>
    <Button
      variant="outline"
      size="sm"
      onClick={onReconnect}
      className="h-6 flex items-center gap-1.5 px-2 py-1"
    >
      <RefreshCw className="h-3 w-3" />
      <span className="text-xs">Reconnect</span>
    </Button>
  </div>
)

type SinglePodLogsLayoutProps = {
  instanceId: number
  podName: string
  containerName: string
}

const SinglePodLogsLayout = ({
  instanceId,
  podName,
  containerName,
}: SinglePodLogsLayoutProps) => {
  const { isConnected, reconnect } = useServerLogs(
    instanceId,
    podName,
    containerName
  )

  return (
    <div className="flex h-[500px] border rounded-lg overflow-hidden bg-card">
      <div className="flex-1 flex flex-col">
        <LogsHeader
          podName={podName}
          isConnected={isConnected}
          onReconnect={reconnect}
        />
        <LogsTabContent
          instanceId={instanceId}
          podName={podName}
          containerName={containerName}
          showHeading={false}
          showHeader={false}
        />
      </div>
    </div>
  )
}

type LogsTabContentProps = {
  instanceId: number
  podName: string
  containerName: string
  showHeading?: boolean
  showHeader?: boolean
}

const LogsTabContent = ({
  instanceId,
  podName,
  containerName,
  showHeading = false,
  showHeader = false,
}: LogsTabContentProps) => {
  const { logs, isConnected, reconnect } = useServerLogs(
    instanceId,
    podName,
    containerName
  )
  const { theme } = useTheme()

  const editorRef = useRef<unknown>(null)

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (editorRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const editor = editorRef.current as any
      const lineCount = editor.getModel()?.getLineCount() || 0
      editor.revealLine(lineCount)
    }
  }, [logs])

  const handleEditorDidMount = (editor: unknown) => {
    editorRef.current = editor
    // Auto-scroll to bottom on mount
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editorInstance = editor as any
    const lineCount = editorInstance.getModel()?.getLineCount() || 0
    editorInstance.revealLine(lineCount)
  }

  // Resolve the actual theme for Monaco editor
  const getMonacoTheme = () => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'vs-dark'
        : 'vs'
    }
    return theme === 'dark' ? 'vs-dark' : 'vs'
  }

  return (
    <div className="flex flex-col h-full">
      {showHeader && (
        <div className="flex items-center justify-between p-3 border-b bg-muted/20">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">
              Server Logs
            </h3>
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-destructive'
              }`}
              title={isConnected ? 'Live • Connected' : 'Disconnected'}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={reconnect}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Reconnect
          </Button>
        </div>
      )}
      {showHeading && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">
              Server Logs
            </h3>
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-destructive'
              }`}
              title={isConnected ? 'Live • Connected' : 'Disconnected'}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={reconnect}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Reconnect
          </Button>
        </div>
      )}
      <div
        className={`overflow-hidden flex-1 ${showHeader ? 'border m-3 mt-0' : 'border'}`}
      >
        <LazyMonacoEditor
          height="100%"
          defaultLanguage="shell"
          value={logs.join('\n')}
          theme={getMonacoTheme()}
          options={{
            readOnly: true,
            scrollBeyondLastLine: false,
            minimap: { enabled: false },
            wordWrap: 'on',
            scrollbar: {
              vertical: 'auto',
              horizontal: 'auto',
            },
            fontSize: 12,
            fontFamily:
              'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
            automaticLayout: true,
            renderLineHighlight: 'none',
            selectionHighlight: false,
            occurrencesHighlight: false,
            renderControlCharacters: false,
            renderWhitespace: 'none',
            folding: false,
            lineNumbers: 'on',
            glyphMargin: false,
            padding: {
              top: 12,
              bottom: 12,
              left: 16,
              right: 16,
            },
            // Disable unnecessary editor features for log viewing
            quickSuggestions: false,
            suggestOnTriggerCharacters: false,
            acceptSuggestionOnEnter: 'off',
            tabCompletion: 'off',
            wordBasedSuggestions: false,
            parameterHints: { enabled: false },
            autoIndent: 'none',
            formatOnType: false,
            formatOnPaste: false,
            dragAndDrop: false,
            links: false,
            colorDecorators: false,
            codeLens: false,
            contextmenu: false,
            mouseWheelZoom: false,
            multiCursorModifier: 'ctrlCmd',
            accessibilitySupport: 'off',
            find: {
              addExtraSpaceOnTop: false,
              autoFindInSelection: 'never',
              seedSearchStringFromSelection: 'never',
            },
            gotoLine: { enabled: false },
            hover: { enabled: false },
            lightbulb: { enabled: false },
            bracketPairColorization: { enabled: false },
            guides: {
              bracketPairs: false,
              indentation: false,
              highlightActiveIndentation: false,
            },
            unicodeHighlight: {
              ambiguousCharacters: false,
              invisibleCharacters: false,
            },
            unusualLineTerminators: 'off',
            showUnused: false,
            showDeprecated: false,
          }}
          onMount={handleEditorDidMount}
        />
      </div>
    </div>
  )
}
